import React, { useState, SyntheticEvent, ChangeEvent, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import ControlledInput from '../../../../../components/ControlledInput';
import environment from '../../../../../environment';
import {
  IInventoryChannel,
  IInventoryData,
  IInventoryItem,
  ISubInventory
} from '../../../../../interfaces/IInventoryItem';
import GenericModal from '../../../../../components/GenericModal';

interface ISubInventoryProps {
  productCode: string;
  productDescription: string;
  channel: string;
  subinventory: ISubInventory;
  inventoryData: IInventoryData;
  setInventoryData: CallableFunction;
}

const PreorderReleaseModal: React.FC<ISubInventoryProps> = ({
  productCode,
  productDescription,
  channel,
  subinventory,
  inventoryData,
  setInventoryData
}) => {
  const getAvailableQty = (allocated = subinventory.allocated) =>
    Math.min(subinventory.stockOnHand, subinventory.reserved - allocated);

  const [availableQuantity, setAvailableQuantity] = useState(getAvailableQty());
  const [releaseQuantity, setReleaseQuantity] = useState(availableQuantity);
  const [releaseQuantityError, setReleaseQuantityError] = useState('');

  useEffect(() => {
    const newQty = getAvailableQty();
    setAvailableQuantity(newQty);
    setReleaseQuantity(newQty);
  }, [inventoryData]);

  const clearForm = () => {
    setReleaseQuantity(availableQuantity);
    setReleaseQuantityError('');
  };

  const handleReleaseQuantityChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const qty = parseInt(value, 10);

    setReleaseQuantity(qty);

    if (!value || isNaN(qty) || qty <= 0) {
      setReleaseQuantityError('Required field');
    } else if (qty > availableQuantity) {
      setReleaseQuantityError(
        'Insufficient stock or more preorders than reserved'
      );
    } else {
      setReleaseQuantityError('');
    }
  };

  const updateInventoryData = (newAllocated: number) => {
    setInventoryData({
      ...inventoryData,
      items: inventoryData.items.map((item: IInventoryItem) =>
        item.productCode === productCode
          ? {
              ...item,
              channels: item.channels.map((ch: IInventoryChannel) =>
                ch.channelName === channel
                  ? {
                      ...ch,
                      subinventories: ch.subinventories.map(
                        (sb: ISubInventory) =>
                          sb.subinventory === subinventory.subinventory
                            ? { ...sb, allocated: newAllocated }
                            : sb
                      )
                    }
                  : ch
              )
            }
          : item
      )
    });
  };

  const handleSubmit =
    (
      setLoading: (loading: boolean) => void,
      setOpen: (open: boolean) => void
    ) =>
    async (event: SyntheticEvent) => {
      event.preventDefault();
      setLoading(true);

      try {
        const res = await axios.post(
          `${environment.apiPath}releasePreorders`,
          {
            productCodes: [productCode],
            subinventory: subinventory.subinventory,
            releaseQuantity
          },
          { ...environment.params }
        );

        const { totalAllocated, requestedQuantity, releasedQuantity } =
          res.data;

        const newQty = getAvailableQty(totalAllocated);
        setAvailableQuantity(newQty);
        setReleaseQuantity(newQty);
        updateInventoryData(totalAllocated);

        if (requestedQuantity === releasedQuantity) {
          toast.success(
            `Successfully released pre-orders for SKU ${productCode}`
          );
        } else {
          toast.warning(
            `Requested ${requestedQuantity}. Only ${releasedQuantity} pre-orders released for SKU ${productCode}`
          );
        }
      } catch (err: AxiosError | any) {
        toast.error(err.message);
        clearForm();
      } finally {
        setOpen(false);
        setLoading(false);
      }
    };

  return (
    <GenericModal
      btnTitle='Release preorders'
      clearForm={clearForm}
      formTitle={productCode}
      disabled={releaseQuantity <= 0}
      formSubtitle={<h3>{productDescription}</h3>}
      formDescription={`${channel} - ${subinventory.subinventory}`}
      formError={!!releaseQuantityError}
      classes='btn--small'
      handleSubmit={handleSubmit}
    >
      <ControlledInput
        id='releaseQuantity'
        label='Release quantity'
        type='number'
        value={releaseQuantity}
        placeholder='Type release quantity here'
        handleChange={handleReleaseQuantityChange}
        error={releaseQuantityError}
        required
      />
    </GenericModal>
  );
};

export default PreorderReleaseModal;
